import { useReactiveVar } from "@apollo/client";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogOverlay,
  Button,
  IconButton,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import gql from "graphql-tag";
import { uniq } from "lodash";
import React from "react";

import {
  ASSET_CATEGORY_HIDDEN_MESSAGE,
  ASSET_CATEGORY_SHOWN_MESSAGE,
} from "../../constants/lang/en";
import {
  AssetCategoryFragmentFragment,
  useCompanyQuery,
  useCompanyUpdateHiddenCategoryIdsMutation,
} from "../../graphql/graphql";
import { assetCategoryIdsWithAssetsVar } from "../../graphql/reactiveVariables";
import { setGenericMessage } from "../../utils/serverErrors";

interface AssetCategoryVisibilityToggleProps {
  assetCategory: AssetCategoryFragmentFragment;
}

const AssetCategoryVisibilityToggle: React.FC<
  AssetCategoryVisibilityToggleProps
> = ({ assetCategory }) => {
  const { data } = useCompanyQuery();
  const assetCategoryIdsWithAssets = useReactiveVar(
    assetCategoryIdsWithAssetsVar
  );
  const hiddenCategoryIds = React.useMemo(
    () => (data ? data.company.hiddenCategoryIds : []),
    [data]
  );
  const isHidden = hiddenCategoryIds.includes(assetCategory.id);
  const toast = useToast();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [assetCategoryVisibilityToggleMutation, { loading }] =
    useCompanyUpdateHiddenCategoryIdsMutation();

  const actionAllowed =
    isHidden || !assetCategoryIdsWithAssets.includes(assetCategory.id);

  const toggleAssetCategoryVisibility = React.useCallback(async () => {
    try {
      const { errors } = await assetCategoryVisibilityToggleMutation({
        variables: {
          hiddenCategoryIds: uniq(
            isHidden
              ? hiddenCategoryIds.filter((i) => i !== assetCategory.id)
              : [...hiddenCategoryIds, assetCategory.id]
          ),
        },
      });
      if (errors) {
        toast({
          description: setGenericMessage(errors),
          status: "error",
          position: "top",
          isClosable: true,
        });
      } else {
        onClose();
        toast({
          description: isHidden
            ? ASSET_CATEGORY_SHOWN_MESSAGE
            : ASSET_CATEGORY_HIDDEN_MESSAGE,
          status: "success",
          position: "top",
          isClosable: true,
        });
        return;
      }
    } catch (error) {
      toast({
        description: setGenericMessage(error),
        status: "error",
        position: "top",
        isClosable: true,
      });
    }
  }, [
    assetCategoryVisibilityToggleMutation,
    toast,
    onClose,
    hiddenCategoryIds,
    isHidden,
    assetCategory.id,
  ]);

  return (
    <>
      <Tooltip hasArrow label={`${isHidden ? "Unhide" : "Hide"} this category`}>
        <IconButton
          onClick={onOpen}
          variant="icon"
          aria-label={`${isHidden ? "Unhide" : "Hide"} this category`}
          colorScheme="graySecondary"
          padding={2}
        >
          <FontAwesomeIcon icon={isHidden ? faEye : faEyeSlash} />
        </IconButton>
      </Tooltip>
      <AlertDialog
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isOpen={isOpen}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogBody textAlign="center">
              {actionAllowed ? (
                <>
                  Are you sure you want to {isHidden ? "unhide" : "hide"} this
                  category?
                  <br />
                  <Text as="strong">{assetCategory.name}</Text>
                </>
              ) : (
                "Sorry, you can't hide this asset category because there are existing assets for this category"
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              {actionAllowed ? (
                <>
                  <Button
                    ref={cancelRef}
                    disabled={loading}
                    onClick={onClose}
                    width="48%"
                  >
                    No
                  </Button>
                  <Button
                    onClick={toggleAssetCategoryVisibility}
                    disabled={loading}
                    colorScheme="red"
                    ml="4%"
                    width="48%"
                  >
                    Yes
                  </Button>
                </>
              ) : (
                <Button
                  ref={cancelRef}
                  disabled={loading}
                  onClick={onClose}
                  width="100%"
                >
                  Ok
                </Button>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default AssetCategoryVisibilityToggle;

gql`
  mutation CompanyUpdateHiddenCategoryIds(
    $hiddenCategoryIds: [String!]
    $hiddenTypeIds: [String!]
  ) {
    companyUpdateHiddenCategoryIds(
      hiddenCategoryIds: $hiddenCategoryIds
      hiddenTypeIds: $hiddenTypeIds
    ) {
      ...CompanyFragment
    }
  }
`;
